import React from 'react';
import useOrganizationClientRecordInvoices from '../../../hooks/api/useOrganizationClientRecordInvoices';
import { OrganizationTypes } from '../../../primary-record-types';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import RichTable from '../../v2BaseComponents/RichTable';
import { getIntl } from '../../../utils/intl';
import { TableConfig } from '../../v2BaseComponents/RichTable/types';
import getDateString from '../../../utils/getDateString';
import _truncate from 'lodash/truncate';
import { useRichTableState } from '../../v2BaseComponents/RichTable/useRichTableState';
import IsDisabledProvider from '../../v2BaseComponents/IsDisabledProvider';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import * as Linking from 'expo-linking';
import { Platform } from 'react-native';
import { centsToDollarsConversion } from '../../../utils/centsToDollarsConversion';
import { invoiceStatusTypeGuards } from '../../../utils/invoiceStatusTypeGuards';
import { usePromptButton } from '../PromptButton';
import { PromptActions } from '../../v2BaseComponents/Prompt';
import Text from '../../v2BaseComponents/Text';
import { ContentStack } from '../../v2BaseComponents/Stack';

const intl = getIntl('client-invoices-list', {
    created: 'Created',
    dueDate: 'Due',
    description: 'Description',
    amount: 'Amount',
    status: 'Status',
    yes: 'Yes',
    voidedAt: 'Voided - {date}',
    paidAt: 'Paid - {date}',
    open: 'Open',
    paid: 'Paid',
    void: 'Voided',
    voidInvoice: 'Void',
    resend: 'Resend',
    marked: 'Marked',
    completed: 'Completed At',
    copyInvoiceLink: 'Copy Invoice Link',
    markPaid: 'Mark Paid',
    openInvoicePDF: 'Download Invoice PDF',
    refund: 'Refund',
    refunded: 'Refunded',
    refundInvoice: 'Refund Invoice?',
    confirmationMessage: 'Are you sure you want to refund this invoice?  This action cannot be undone.',
    cancel: 'Cancel',
});

export type ClientInvoicesListOwnProps = {
    clientRecordId: string;
    noPaper?: boolean;
};

export type ClientInvoicesListProps = ClientInvoicesListOwnProps;

type TableItem = OrganizationTypes.OrganizationInvoice;

const defaultData: TableItem[] = [];

const tableConfig: TableConfig<TableItem> = {
    columns: [
        {
            label: intl('created'),
            columnKey: 'createdDateTime',
            render: (item) => getDateString(item.createdDateTime, 'date'),
            getContentKey: (item) => getDateString(item.createdDateTime, 'date'),
            getSearchValue: (item) => getDateString(item.createdDateTime, 'date'),
            getSortValue: (item) => new Date(item.createdDateTime),
        },
        {
            label: intl('dueDate'),
            columnKey: 'dueDateTime',
            render: (item) => getDateString(item.dueDateTime, 'date'),
            getContentKey: (item) => getDateString(item.dueDateTime, 'date'),
            getSearchValue: (item) => getDateString(item.dueDateTime, 'date'),
            getSortValue: (item) => new Date(item.dueDateTime),
            hideOnMobile: true,
        },
        {
            label: intl('description'),
            columnKey: 'description',
            render: (item) => _truncate(item.description, { length: 50 }),
            getContentKey: (item) => item.description || '',
            getSearchValue: (item) => item.description || '',
            getSortValue: (item) => item.description || '',
            hideOnMobile: true,
        },
        {
            label: intl('amount'),
            columnKey: 'amount',
            render: (item) => `$${centsToDollarsConversion.convertCentsToDollars(item.amount)}`,
            getContentKey: (item) => item.amount.toString(),
            getSearchValue: (item) => centsToDollarsConversion.convertCentsToDollars(item.amount).toString(),
            getSortValue: (item) => item.amount,
        },
        {
            label: intl('status'),
            columnKey: 'status',
            render: (item) => {
                const base = intl(item.status);
                if (invoiceStatusTypeGuards.paid(item) && item.markedPaid) {
                    return `${base} (${intl('marked')})`;
                } else {
                    return base;
                }
            },
            getContentKey: (item) => intl(item.status),
            getSearchValue: (item) => intl(item.status),
            getSortValue: (item) => intl(item.status),
            getDiscreetValue: (item) => intl(item.status),
        },
        {
            label: intl('completed'),
            columnKey: 'completed',
            render: (item) => {
                if (invoiceStatusTypeGuards.void(item) && item.voidDateTime) {
                    return intl('voidedAt', { date: getDateString(item.voidDateTime, 'date-time') });
                } else if (invoiceStatusTypeGuards.paid(item) && item.paidDateTime) {
                    return intl('paidAt', { date: getDateString(item.paidDateTime, 'date-time') });
                }
                return '';
            },
            getSortValue: (item) => {
                if (invoiceStatusTypeGuards.void(item) && item.voidDateTime) {
                    return new Date(item.voidDateTime);
                } else if (invoiceStatusTypeGuards.paid(item) && item.paidDateTime) {
                    return new Date(item.paidDateTime);
                }
                return null;
            },
        },
    ],
    getRowKey: (item) => item.invoiceId,
};

const ClientInvoicesList: React.FC<ClientInvoicesListProps> = ({ clientRecordId, noPaper }) => {
    const { copyToClipboard } = useCopyToClipboard({});
    const [tableState, setTableState] = useRichTableState();
    const {
        isLoadingInvoices,
        invoices,
        voidInvoice,
        isVoidingInvoice,
        markPaidInvoice,
        isMarkingPaidInvoice,
        resendInvoice,
        isResendingInvoice,
        refundInvoice,
        isRefundingInvoice,
    } = useOrganizationClientRecordInvoices({
        clientRecordId: clientRecordId,
    });
    const data: TableItem[] = (invoices?.items || defaultData) as TableItem[];
    const refundConfirmationPromptButton = usePromptButton({
        promptTitle: intl('refundInvoice'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Text variant="body-1">{intl('confirmationMessage')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('refund'),
                            variant: 'primary',
                            icon: 'RefreshCcw',
                            onPress: () => {
                                refundInvoice(data[0].invoiceId);
                                onClose();
                            },
                            testID: 'confirm-refund-invoice',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'X',
                            testID: 'cancel-refund-invoice',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    return (
        <>
            <AnimateLoadedContent isLoading={isLoadingInvoices}>
                {() => (
                    <IsDisabledProvider
                        isDisabled={
                            isVoidingInvoice || isMarkingPaidInvoice || isResendingInvoice || isRefundingInvoice
                        }
                    >
                        <RichTable
                            config={tableConfig}
                            state={tableState}
                            onStateChange={setTableState}
                            data={data}
                            noPaper={noPaper}
                            isFetching={isLoadingInvoices}
                            allowRowSelection
                            minWidth={'100%'}
                            actions={[
                                {
                                    title: intl('voidInvoice'),
                                    icon: 'XCircle',
                                    shouldShow: (items) => items.length === 1 && invoiceStatusTypeGuards.open(items[0]),
                                    onPress: (items) => voidInvoice(items[0].invoiceId),
                                    testID: 'void-invoice-button',
                                },
                                {
                                    title: intl('resend'),
                                    icon: 'Send',
                                    shouldShow: (items) => items.length === 1 && invoiceStatusTypeGuards.open(items[0]),
                                    onPress: (items) => {
                                        resendInvoice(items[0].invoiceId);
                                    },
                                    testID: 'resend-invoice-button',
                                },
                                {
                                    title: intl('markPaid'),
                                    icon: 'CheckCircle',
                                    shouldShow: (items) => items.length === 1 && invoiceStatusTypeGuards.open(items[0]),
                                    onPress: (items) => {
                                        markPaidInvoice(items[0].invoiceId);
                                    },
                                    testID: 'mark-paid-invoice-button',
                                },
                                {
                                    title: intl('copyInvoiceLink'),
                                    icon: 'Copy',
                                    shouldShow: (items) => items.length === 1 && invoiceStatusTypeGuards.open(items[0]),
                                    onPress: (items) => {
                                        const invoice = items[0];
                                        if (invoiceStatusTypeGuards.open(invoice)) {
                                            copyToClipboard(invoice.paymentUrl);
                                        }
                                    },
                                },
                                {
                                    title: intl('refund'),
                                    icon: 'RefreshCcw',
                                    shouldShow: (items) => items.length === 1 && invoiceStatusTypeGuards.paid(items[0]),
                                    onPress: refundConfirmationPromptButton.onPress,
                                    testID: 'refund-invoice-button',
                                },
                                {
                                    title: intl('openInvoicePDF'),
                                    icon: 'DownloadCloud',
                                    shouldShow: (items) => items.length === 1,
                                    onPress: (items) => {
                                        const invoice = items[0];
                                        if (Platform.OS === 'web') {
                                            window.open(invoice.pdfUrl, '_blank');
                                        } else {
                                            Linking.openURL(invoice.pdfUrl);
                                        }
                                    },
                                },
                            ]}
                        />
                    </IsDisabledProvider>
                )}
            </AnimateLoadedContent>
            {refundConfirmationPromptButton.Prompt}
        </>
    );
};

export default React.memo(ClientInvoicesList);
